import { useCallback, useMemo, useState, useEffect } from 'react'
import { useHistory, generatePath } from 'react-router-dom'
import ROUTES from 'app/routes'
import NetworkWizardLayout from '../../NetworkWizardLayout'
import {
  WLANMainBody,
  WLANTextBody,
  WLANTemplateName,
  WLANError,
} from './styles'
import {
  CreateWLANsContainer,
  CreateWLANsHeaderWrapper,
  StyledNetworkWizardBottomPanel,
} from '../styles'
import {
  NetworkWizardCancelButton,
  NetworkWizardSubmitButton,
  NetworkWizardTitle,
} from '../../styles'
import { WhiteBlock } from 'components/Layout'
import { useIntl } from 'react-intl'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import {
  useNetworkHomeJobsRouteParams,
  useCurrentHomeSiteId,
  useNetworkOrgId,
} from 'features/networkAi/hooks'
import { orgSiteWlansFetchDataSelector } from 'features/networkAi/selectors'
import { commonMessages } from 'app/i18n/messages'
import { createWLANSaveTemplateMessages } from './messages'
import { createOrgTemplateThunk } from 'features/networkAi/slice'
import {
  OrgTemplates,
  OrgSiteWlans,
  CreateWLANBody,
} from 'features/networkAi/types'
import { DEFAULT_WLAN_VALUES } from 'features/networkAi/consts'
import { SpinnerCentered } from 'components/Loader'
import { fetchOrgSiteWlansThunk } from 'features/networkAi/slice'

function CreateWLANSaveTemplate() {
  const { formatMessage } = useIntl()
  const history = useHistory()
  const dispatch = useAppDispatch()
  const orgId = useNetworkOrgId()
  const { rootDomainId, jobId, homeId } = useNetworkHomeJobsRouteParams()
  const siteId = useCurrentHomeSiteId({ homeId })
  const orgSiteWlans = useAppSelector(orgSiteWlansFetchDataSelector.data)
  const isLoading = useAppSelector(orgSiteWlansFetchDataSelector.isFetching)
  const [showError, setShowError] = useState(false)
  const [templateSaveName, setTemplateSaveName] = useState('')

  function handleChange(event: any) {
    setTemplateSaveName(event.target.value)
    setShowError(false)
  }

  interface createTemplateFormValues {
    name: string
  }

  const handleBackButtonClick = useCallback(() => {
    const path = generatePath(ROUTES.networkAi.wizard.createWLANsSetup, {
      rootDomainId,
      jobId,
      homeId,
    })

    return history.push(path)
  }, [history, rootDomainId, jobId, homeId])

  const handleNextDoneClick = useCallback(async () => {
    const orgSiteWlansWithNew: OrgSiteWlans[] = Array.isArray(orgSiteWlans)
      ? orgSiteWlans
      : [{} as OrgSiteWlans]

    const newWLANBodies: Array<CreateWLANBody> = []
    const mapTemplateRequest = (values: CreateWLANBody): CreateWLANBody => {
      return Object.keys(values).reduce((acc, key) => {
        let newValues: CreateWLANBody = { ...acc }
        switch (key) {
          case 'id':
            newValues.id = ''
            break
          case 'auth':
            newValues.auth.psk = 'MustChangeMe!'
            break
          case 'portal':
            newValues.portal.password = 'MustChangeMe!'
            break
          default:
            newValues = {
              ...newValues,
              [key]: values[key as keyof CreateWLANBody],
            }
        }
        return newValues
      }, DEFAULT_WLAN_VALUES as CreateWLANBody)
    }

    if (orgSiteWlansWithNew != null) {
      orgSiteWlansWithNew.forEach(async function (orgSiteWlan) {
        if (orgSiteWlan.id === siteId) {
          orgSiteWlan.wlans.forEach(async function (wlan) {
            const newWLANBody = mapTemplateRequest(wlan)
            newWLANBodies.push(newWLANBody)
          })
        }
      })
    }

    const mapNewTemplateToRequest = (
      values: createTemplateFormValues
    ): OrgTemplates => {
      return Object.keys(values).reduce(
        (acc, key) => {
          let newValues: OrgTemplates = { ...acc }
          switch (key) {
            case 'name':
              newValues.name = templateSaveName
              break
            default:
              newValues = {
                ...newValues,
                [key]: values[key as keyof createTemplateFormValues],
              }
          }
          return newValues
        },
        {
          name: '',
          siteId: siteId,
          job: jobId,
          homeId: homeId,
          orgId: orgId,
          wlans: newWLANBodies,
        } as OrgTemplates
      )
    }

    const values: createTemplateFormValues = { name: templateSaveName }
    const newTemplateBody = mapNewTemplateToRequest(values)
    try {
      await dispatch(createOrgTemplateThunk(orgId, newTemplateBody))

      const path = generatePath(ROUTES.networkAi.wizard.createWLANs, {
        rootDomainId,
        jobId,
        homeId,
        siteId,
      })

      return history.push(path)
    } catch (error) {
      console.log('Error saving template', error)
      setShowError(true)
    }
  }, [
    history,
    dispatch,
    rootDomainId,
    jobId,
    homeId,
    siteId,
    templateSaveName,
    orgId,
    orgSiteWlans,
  ])

  const bottomBlock = useMemo(() => {
    return (
      <StyledNetworkWizardBottomPanel>
        <NetworkWizardCancelButton
          variant="secondary"
          type="button"
          onClick={handleBackButtonClick}
        >
          {formatMessage(commonMessages.back)}
        </NetworkWizardCancelButton>
        <NetworkWizardSubmitButton
          variant="primary"
          type="submit"
          disabled={false}
          onClick={handleNextDoneClick}
        >
          {formatMessage(commonMessages.done)}
        </NetworkWizardSubmitButton>
      </StyledNetworkWizardBottomPanel>
    )
  }, [handleBackButtonClick, handleNextDoneClick, formatMessage])

  useEffect(() => {
    orgId && dispatch(fetchOrgSiteWlansThunk(orgId))
  }, [dispatch, orgId])

  return (
    <NetworkWizardLayout bottomBlock={bottomBlock} currentWizardStepId={5}>
      <CreateWLANsContainer>
        <CreateWLANsHeaderWrapper>
          <NetworkWizardTitle>Save Network Template</NetworkWizardTitle>
        </CreateWLANsHeaderWrapper>

        {isLoading ? (
          <SpinnerCentered mt="200px" />
        ) : (
          <WhiteBlock>
            <WLANMainBody>
              <WLANTextBody>
                {formatMessage(createWLANSaveTemplateMessages.saveInstructions)}
              </WLANTextBody>
              <WLANTextBody>
                {formatMessage(createWLANSaveTemplateMessages.enterName)}
              </WLANTextBody>
              <WLANTemplateName
                placeholder={'Template Name'}
                name="templateName"
                onChange={handleChange}
              />
              {showError && (
                <WLANError>
                  {formatMessage(createWLANSaveTemplateMessages.errorName)}
                </WLANError>
              )}
            </WLANMainBody>
          </WhiteBlock>
        )}
      </CreateWLANsContainer>
    </NetworkWizardLayout>
  )
}

export default CreateWLANSaveTemplate
